import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { killSwitchAPI } from '@/services/api';
import { useRBAC } from '@/lib/rbac';
import type { KillSwitchState, KillSwitchEngageRequest, KillSwitchClearRequest } from '@/../../shared/schema';
import { ApiError } from '@/lib/apiError';

export function useKillSwitch() {
  const queryClient = useQueryClient();
  const rbac = useRBAC();
  const canManageKillSwitch = rbac.hasAnyRole(['org_owner', 'rugira_platform_admin']);
  const canViewKillSwitch = rbac.hasAnyRole([
    'org_owner',
    'rugira_platform_admin',
    'rugira_support',
    'rugira_compliance_admin',
  ]);
  const requiresElevation = canViewKillSwitch && !canManageKillSwitch;
  const enabled = canViewKillSwitch && killSwitchAPI.canUse();

  const killSwitchQuery = useQuery<KillSwitchState | { active: false } | null>({
    queryKey: ['/api/admin/kill-switch/status'],
    enabled,
    refetchInterval: enabled ? 5000 : false,
    queryFn: async () => {
      try {
        return await killSwitchAPI.getStatus();
      } catch (error) {
        const message = error instanceof Error ? error.message.toLowerCase() : '';
        if (message.includes('unauthorized') || message.includes('401')) {
          return null;
        }
        throw error;
      }
    },
  });

  const engageMutation = useMutation({
    mutationFn: async (request: KillSwitchEngageRequest) => {
      if (!killSwitchAPI.canUse()) {
        throw new Error('Kill switch API unavailable');
      }
      return killSwitchAPI.engage(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/kill-switch/status'] });
    },
  });

  const clearMutation = useMutation({
    mutationFn: async (request: KillSwitchClearRequest) => {
      if (!killSwitchAPI.canUse()) {
        throw new Error('Kill switch API unavailable');
      }
      return killSwitchAPI.clear(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/kill-switch/status'] });
    },
  });

  const mergedError =
    killSwitchQuery.error || engageMutation.error || clearMutation.error;
  const lastError =
    mergedError instanceof ApiError ? mergedError : undefined;

  return {
    killSwitchState:
      enabled && killSwitchQuery.data && (killSwitchQuery.data as KillSwitchState)?.active
        ? (killSwitchQuery.data as KillSwitchState)
        : null,
    isLoading: (enabled && killSwitchQuery.isLoading) || engageMutation.isPending || clearMutation.isPending,
    engageKillSwitch: engageMutation.mutateAsync,
    clearKillSwitch: clearMutation.mutateAsync,
    error: mergedError,
    lastApiError: lastError,
    canManageKillSwitch,
    canViewKillSwitch,
    requiresElevation,
  };
}
